<script lang="ts">
	import { clone, type JsonObject, type JsonValue } from "@bufbuild/protobuf";
	import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import P from "#lib/components/P.svelte";
	import Toggle from "#lib/components/Toggle.svelte";
	import { booleanValue, setBooleanValue, setStructValue, structValue } from "#lib/struct.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		onChange: (value?: JsonValue) => void;
		page: Writable<Page>;
		showEnableClick?: boolean;
	}

	let { id, onChange, page, showEnableClick = true }: Props = $props();

	const config = $derived(structValue($page.config, id));
	const disabled = $derived(Boolean(booleanValue(config, "disabled")));
	const hidden = $derived(Boolean(booleanValue(config, "hidden")));

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function handleDisableChanged(e: Event) {
		const nextDisabled = !(e.target as HTMLInputElement).checked;
		const current = getConfig();
		if (booleanValue(current, "disabled") === nextDisabled) {
			return;
		}
		setBooleanValue(current, "disabled", nextDisabled);
		$page = clone(PageSchema, $page);
		onChange?.(nextDisabled);
	}

	function handleVisibilityChanged(e: Event) {
		const nextHidden = !(e.target as HTMLInputElement).checked;
		const current = getConfig();
		if (booleanValue(current, "hidden") === nextHidden) {
			return;
		}
		setBooleanValue(current, "hidden", nextHidden);
		$page = clone(PageSchema, $page);
		onChange?.(nextHidden);
	}
</script>

<div class="flex w-full flex-col gap-2 rounded-lg border p-3">
	<div class="flex flex-col gap-5">
		<P class="text-sm font-bold text-black">State</P>
		<div class="flex flex-col gap-2">
			<div class="ml-4 flex flex-col gap-2">
				{#if showEnableClick}
					<div class="flex flex-row items-start gap-2">
						<Toggle checked={!disabled} on:change={handleDisableChanged} id={`${id}-disabled-toggle`} />
						<div class="flex flex-col">
							<Label for={`${id}-disabled-toggle`}>Enable click</Label>
							<P size="xs">By clicking on this component, consumers will be redirected to the destination</P>
						</div>
					</div>
				{/if}
				<div class="flex flex-row items-start gap-2">
					<Toggle checked={!hidden} on:change={handleVisibilityChanged} id={`${id}-hidden-toggle`} />
					<div class="flex flex-col">
						<Label for={`${id}-hidden-toggle`}>Show on page</Label>
						<P size="xs">Display this component on published page</P>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
